import { useEffect, useRef, useState } from 'react';
import { Button, Select, SelectItem } from '@nextui-org/react';
import { saveFruticBoxToFormatMapping } from '../../api/frutic';
import { useTranslation } from '../../hooks/useTranslation';
import { VALID_FORMATS } from '../../utils/definitions';
import type { BoxFormatMapping } from '../../utils/types';

interface PackingCodeMappingEditorProps {
  exporterId: number;
  codes: string[];
  onMapped: (mapping: BoxFormatMapping) => void;
}

/** The same exporter mapping used by Settings, without leaving the current flow. */
export default function PackingCodeMappingEditor({
  exporterId,
  codes,
  onMapped,
}: PackingCodeMappingEditorProps) {
  const { t } = useTranslation('market');
  const [selectedFormats, setSelectedFormats] = useState<
    Record<string, string>
  >({});
  const [savingCodes, setSavingCodes] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(new Set<string>());
  const mounted = useRef(false);
  const scopeRef = useRef({ exporterId });
  if (scopeRef.current.exporterId !== exporterId)
    scopeRef.current = { exporterId };
  const onMappedRef = useRef(onMapped);
  onMappedRef.current = onMapped;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      scopeRef.current = { exporterId: scopeRef.current.exporterId };
    };
  }, []);
  useEffect(() => {
    setSelectedFormats({});
    setSavingCodes([]);
    setErrors({});
  }, [exporterId]);

  const saveMapping = async (code: string, format: string) => {
    if (!exporterId || !code || !VALID_FORMATS.includes(format)) return;
    const scope = scopeRef.current;
    const key = JSON.stringify([exporterId, code]);
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    const isCurrent = () => mounted.current && scope === scopeRef.current;
    setSelectedFormats((current) => ({ ...current, [code]: format }));
    setSavingCodes((current) => [...current, code]);
    setErrors((current) => ({ ...current, [code]: '' }));
    try {
      const mapping = await saveFruticBoxToFormatMapping(
        { exporter_id: exporterId },
        code,
        format
      );
      if (!isCurrent()) return;
      if (!mapping) throw new Error('Mapping save failed');
      onMappedRef.current(mapping);
    } catch {
      if (isCurrent())
        setErrors((current) => ({
          ...current,
          [code]: t('No se pudo guardar el mapeo de formato'),
        }));
    } finally {
      inFlight.current.delete(key);
      if (isCurrent())
        setSavingCodes((current) => current.filter((item) => item !== code));
    }
  };

  return (
    <div className='flex flex-col gap-3'>
      {Array.from(new Set(codes)).map((code) => (
        <div
          key={code}
          className='flex flex-col gap-2 rounded-medium border border-default-200 p-3'
        >
          <p className='break-words font-medium text-default-700'>{code}</p>
          <Select
            size='sm'
            label={t('Formato')}
            aria-label={t('Formato para {{code}}', { code })}
            placeholder={t('Seleccionar formato')}
            selectedKeys={selectedFormats[code] ? [selectedFormats[code]] : []}
            disallowEmptySelection
            isDisabled={savingCodes.includes(code)}
            isLoading={savingCodes.includes(code)}
            isInvalid={!!errors[code]}
            errorMessage={errors[code]}
            onSelectionChange={(keys) => {
              void saveMapping(code, String(Array.from(keys)[0] ?? ''));
            }}
          >
            {VALID_FORMATS.map((format) => (
              <SelectItem key={format}>{format}</SelectItem>
            ))}
          </Select>
          {errors[code] && (
            <Button
              size='sm'
              variant='light'
              onPress={() => void saveMapping(code, selectedFormats[code])}
            >
              {t('Reintentar')}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
